import { useNavigate } from "react-router-dom";
import { X, Pencil, Trash2, Image as ImageIcon, Plus, Loader2 } from "lucide-react";
import { useState } from "react";
import { EVENT_COLORS } from "@/lib/eventColors";
import api, { formatError } from "@/lib/api";

/**
 * DayPopover — appears when a calendar day with events is tapped.
 * Lists every event on that day with quick Edit / Photos / Delete actions,
 * plus a "+ Add another event" button. The popover sits as a centered modal
 * so it's reachable on both desktop and mobile.
 *
 * Props:
 *   date           — JS Date (the clicked day)
 *   dayEvents      — array of event objects on this day
 *   currentUserId  — used to decide whether the user can edit / delete
 *   onClose()
 *   onEdit(event)  — open EventCreateModal in edit mode
 *   onAdd()        — open EventCreateModal in create mode for this day
 *   onChanged()    — refresh upstream events list after edits/deletes
 */
export default function DayPopover({
  date,
  dayEvents = [],
  currentUserId,
  onClose,
  onEdit,
  onAdd,
  onChanged,
}) {
  const navigate = useNavigate();
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  const label = date.toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });

  const handleDelete = async (ev) => {
    if (!window.confirm(`Delete the event "${ev.title}"? This cannot be undone.`)) return;
    setBusyId(ev.event_id); setErr("");
    try {
      await api.delete(`/events/${ev.event_id}`);
      onChanged && onChanged();
      // If the popover ends up empty, close it.
      if (dayEvents.length <= 1) onClose && onClose();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="day-popover"
    >
      <div
        className="bd-surface p-5 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>
              {dayEvents.length} on this day
            </div>
            <h2 className="text-xl font-bold" style={{ letterSpacing: "-0.02em" }}>
              {label}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="bd-btn bd-btn-ghost"
            style={{ padding: "0.4rem" }}
            data-testid="day-popover-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {err && (
          <div className="text-sm mb-3 px-3 py-2 rounded"
               style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }}
               data-testid="day-popover-error">
            {err}
          </div>
        )}

        <ul className="flex flex-col gap-2 mb-4">
          {dayEvents.map((ev) => {
            const color = EVENT_COLORS[ev.event_type] || EVENT_COLORS.general;
            const isCreator = currentUserId && ev.created_by === currentUserId;
            return (
              <li
                key={`${ev.event_id}-${ev.date}`}
                className="flex items-center gap-2 rounded"
                style={{
                  background: "var(--bg-soft)",
                  padding: "0.5rem 0.6rem",
                  borderLeft: `4px solid ${color}`,
                }}
                data-testid={`day-popover-event-${ev.event_id}`}
              >
                <button
                  className="flex-1 min-w-0 text-left"
                  style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                  onClick={() => navigate(`/events/${ev.event_id}`)}
                  data-testid={`day-popover-open-${ev.event_id}`}
                >
                  <div className="font-semibold truncate" style={{ color: "var(--text)" }}>
                    {ev.title}
                  </div>
                  <div className="text-xs flex items-center gap-2" style={{ color: "var(--text-dim)" }}>
                    <span
                      style={{
                        display: "inline-block", width: 8, height: 8, borderRadius: 9999,
                        background: color,
                      }}
                    />
                    {ev.event_type}
                    {ev.time ? ` · ${ev.time}` : ""}
                    {ev.is_occurrence ? ` · annual` : ""}
                  </div>
                </button>
                <button
                  className="bd-btn bd-btn-ghost"
                  style={{ padding: "0.3rem 0.45rem" }}
                  title="Photos & media"
                  onClick={() => navigate(`/events/${ev.event_id}#media`)}
                  data-testid={`day-popover-media-${ev.event_id}`}
                >
                  <ImageIcon size={12} />
                </button>
                {isCreator && (
                  <>
                    <button
                      className="bd-btn bd-btn-ghost"
                      style={{ padding: "0.3rem 0.45rem" }}
                      title="Edit"
                      onClick={() => onEdit && onEdit(ev)}
                      data-testid={`day-popover-edit-${ev.event_id}`}
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      className="bd-btn bd-btn-ghost"
                      style={{ padding: "0.3rem 0.45rem", color: "#ff3b30" }}
                      title="Delete"
                      onClick={() => handleDelete(ev)}
                      disabled={busyId === ev.event_id}
                      data-testid={`day-popover-delete-${ev.event_id}`}
                    >
                      {busyId === ev.event_id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    </button>
                  </>
                )}
              </li>
            );
          })}
        </ul>

        <button
          className="bd-btn w-full"
          onClick={() => { onAdd && onAdd(); }}
          data-testid="day-popover-add"
        >
          <Plus size={14} /> Add another event on this day
        </button>
      </div>
    </div>
  );
}
